import React, { createContext, useContext } from "react";
import useSoftphone from "../hooks/useSoftphone";

/**
 * One softphone per browser tab.
 *
 * `useSoftphone` owns a SIP registration, so calling it from two components would
 * register the same agent twice — and a provider with two live registrations for
 * one identity forks incoming calls unpredictably between them. This context
 * exists to make that impossible: the hook runs once here, everything else reads.
 *
 * Two consumers today:
 *   • `Softphone` — the widget.
 *   • `useClickToCall` — which needs `effectiveMode` to decide whether to ask the
 *     backend for a browser call or downgrade this one call to the phone.
 *
 * The default value is a working phone-mode object rather than `undefined`, so a
 * component rendered outside the provider degrades to phone mode instead of
 * throwing. A crash here would take out the dial button, which is the one thing
 * that must keep working.
 */

const FALLBACK = {
  loading: false,
  config: null,
  status: "idle",
  reason: "",
  activeCall: null,
  available: false,
  mode: "phone",
  effectiveMode: "phone",
  canChooseMode: false,
  answer: () => {},
  hangup: () => {},
  toggleMute: () => {},
  toggleHold: () => {},
  sendDtmf: () => {},
  setPreferredMode: async () => {},
  reload: () => {},
  retry: () => {},
};

const SoftphoneContext = createContext(FALLBACK);

/** Runs the hook. Mounted only when a softphone may exist at all. */
function ActiveSoftphoneProvider({ children }) {
  const softphone = useSoftphone();
  return (
    <SoftphoneContext.Provider value={softphone}>
      {children}
    </SoftphoneContext.Provider>
  );
}

/**
 * `enabled` gates whether the hook runs at all, rather than the hook checking
 * internally — a logged-out CMS visitor or a student would otherwise fire an
 * authenticated request they cannot make, and apiClient turns a 401 into a logout
 * redirect. A public marketing page must not bounce to the login screen because
 * a softphone asked a question.
 */
export function SoftphoneProvider({ enabled = true, children }) {
  if (!enabled) {
    return (
      <SoftphoneContext.Provider value={FALLBACK}>
        {children}
      </SoftphoneContext.Provider>
    );
  }
  return <ActiveSoftphoneProvider>{children}</ActiveSoftphoneProvider>;
}

export const useSoftphoneContext = () => useContext(SoftphoneContext);

export default SoftphoneContext;
